import { assetType, DeviceBase, DeviceType, HwType } from "types";

/**
 * Single source of truth for converting between a device's non-deprecated
 * `specification.hw_type` ("mobile" | "physical" | "mock") and the synthesized,
 * legacy `type` field ("asset.mobile" | "asset.physical" | "asset.mock").
 *
 * `getDeviceHwType` is the read seam the app should migrate towards: it prefers
 * the non-deprecated `hw_type` and only falls back to deriving it from `type`
 * for data that does not carry a specification yet (v1 data, mocks, fixtures).
 * A future refactor can drop the `type` fallback once every read site is migrated.
 */

/** hw_type -> synthesized legacy device type. */
export const hwTypeToDeviceType = (hwType: HwType | undefined): DeviceType | undefined => {
  switch (hwType) {
    case "physical":
      return assetType.Physical;
    case "mobile":
      return assetType.Mobile;
    case "mock":
      return assetType.Mock;
    default:
      return undefined;
  }
};

/**
 * Legacy device type -> hw_type. Anchors and unknown values yield undefined.
 * Accepts a loose `string` so it can be used over heterogeneous collections
 * (e.g. map items whose `type` is widened to `string`).
 */
export const deviceTypeToHwType = (type: string | undefined): HwType | undefined => {
  switch (type) {
    case assetType.Physical:
      return "physical";
    case assetType.Mobile:
      return "mobile";
    case assetType.Mock:
      return "mock";
    default:
      return undefined;
  }
};

/**
 * Returns the device's hw_type, preferring the non-deprecated
 * `specification.hw_type` and falling back to deriving it from the legacy `type`.
 */
export const getDeviceHwType = (device: {
  type?: string;
  specification?: DeviceBase["specification"];
}): HwType | undefined => {
  return device.specification?.hw_type ?? deviceTypeToHwType(device.type);
};
